import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { FlexRow } from 'ui-components/components/flex';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { PageEventSourceStrings } from '../../hooks/use-track-event';
import { usePaymentAndSubscriptionPageContext } from '../../context/payment-and-subscription-page-context';
import ErrorBoundary from '../../../../../components/error-boundary';
import { useModal } from '../../../../../hooks/use-modal';
import { useWallet } from '../../../../../wallet/hooks/use-wallet';
import { ManagePaymentMethodsWrapper } from '../manage-payment-methods';
import { SubscriptionRow } from './subscription-row';
import { WalletRow } from './wallet-row';
import { useDeprecatedLocalStorage, ADD_PAYMENT_METHOD_DATA } from '../../../../../hooks/use-local-storage';
import { pollForNewPaymentMethod } from '../../../../../financial-services-ui/poll-for-new-payment-method';

import { paymentTableMessages } from '../../messages';
import style from '../../style.module.less';

export const sourceString = PageEventSourceStrings.PAYMENTS_TABLE;

export const PaymentsTableAuctanePay = () => {
  const { billingInfo, isBillingInfoLoading, paymentMethodDetails, reloadData } = usePaymentAndSubscriptionPageContext();
  const [isPolling, setIsPolling] = useState(false);
  const [preloadedSession, setPreloadedSession] = useState(null);
  const [addPaymentMethodData, setAddPaymentMethodData] = useDeprecatedLocalStorage(ADD_PAYMENT_METHOD_DATA);

  const wallet = useWallet();
  // this can be empty if they literally have no wallet record
  // to my knowledge that's an error state, but who can say for sure
  const walletExists = !!wallet;
  const { isWalletRegistered } = wallet ?? {};

  const showManagePaymentMethodsButton = Boolean(isWalletRegistered || billingInfo?.isTokenized);

  const {
    isOpen: isManagePaymentMethodsModalOpen,
    open: openManagePaymentMethodsModal,
    close: closeManagePaymentMethodsModal,
  } = useModal();

  const _onConfirm = useCallback(() => {
    setIsPolling(true);
    pollForNewPaymentMethod({
      reloadData,
      previousCount: paymentMethodDetails?.length ?? 0,
    }).finally(() => {
      setIsPolling(false);
    });
  }, [reloadData, paymentMethodDetails]);

  useEffect(() => {
    if (addPaymentMethodData?.preloadedSession) {
      setPreloadedSession(addPaymentMethodData.preloadedSession);
      setAddPaymentMethodData({});
      openManagePaymentMethodsModal();
    }
  }, [addPaymentMethodData, openManagePaymentMethodsModal, setAddPaymentMethodData]);

  return (
    <ErrorBoundary errorMessageId='paymentsTableError'>
      <div className={classnames(style['new-payments-table'], style['ap-payments-table'])}>
        <FlexRow className={style['title-wrapper']}>
          <div className={style['table-title']}>
            <FormattedMessage {...paymentTableMessages.paymentSettingsTitle} />
          </div>
          {showManagePaymentMethodsButton && (
            <Button onClick={openManagePaymentMethodsModal} theme={THEME_CONSTANTS.link}>
              <FormattedMessage {...paymentTableMessages.managePaymentMethod} />
            </Button>
          )}
        </FlexRow>
        {isBillingInfoLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            {walletExists && <WalletRow />}
            <SubscriptionRow />
          </>
        )}
        {isManagePaymentMethodsModalOpen && (
          <ManagePaymentMethodsWrapper
            key={paymentMethodDetails}
            activePaymentMethods={paymentMethodDetails}
            isLoading={isBillingInfoLoading || isPolling}
            onClose={closeManagePaymentMethodsModal}
            onConfirm={_onConfirm}
            preloadedSession={preloadedSession}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};
